import React, { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = 'https://imselling-marketplace-production.up.railway.app/api';

const STATUS_COLORS = {
  Approved: { bg: '#dcfce7', color: '#16a34a' },
  Pending:  { bg: '#fef9c3', color: '#ca8a04' },
  Flagged:  { bg: '#fee2e2', color: '#dc2626' },
};

function ReviewsView() {
  const [reviews, setReviews]             = useState([]);
  const [stats, setStats]                 = useState({ total: 0, approved: 0, pending: 0, flagged: 0 });
  const [loading, setLoading]             = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // id of row being actioned
  const [error, setError]                 = useState(null);
  const [search, setSearch]               = useState('');
  const [statusFilter, setStatusFilter]   = useState('All');
  const [ratingFilter, setRatingFilter]   = useState('All');
  const [selectedReview, setSelectedReview] = useState(null);

  // ── helpers ────────────────────────────────────────────────────────────────
  const token = () => localStorage.getItem('token');

  const authHeaders = () => ({
    'Authorization': `Token ${token()}`,
    'Content-Type': 'application/json',
  });

  // ── fetch stats + both lists ───────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsRes, ratingsRes, reportsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/reviews/stats/`,   { headers: authHeaders() }),
        fetch(`${API_BASE_URL}/admin/reviews/ratings/?page_size=200`, { headers: authHeaders() }),
        fetch(`${API_BASE_URL}/admin/reviews/reports/?page_size=200`, { headers: authHeaders() }),
      ]);

      if (!statsRes.ok || !ratingsRes.ok || !reportsRes.ok) {
        throw new Error('Failed to fetch reviews data');
      }

      const [statsData, ratingsData, reportsData] = await Promise.all([
        statsRes.json(),
        ratingsRes.json(),
        reportsRes.json(),
      ]);

      setStats(statsData);

      // Combine ratings + reports into one unified list
      const ratings = (ratingsData.results || []);
      const reports = (reportsData.results || []);
      setReviews([...ratings, ...reports]);

    } catch (err) {
      console.error('❌ ReviewsView fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── client-side filter ─────────────────────────────────────────────────────
  const filtered = reviews.filter(r => {
    const matchSearch =
      r.product.toLowerCase().includes(search.toLowerCase()) ||
      r.user.toLowerCase().includes(search.toLowerCase())    ||
      r.comment.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All' || r.status === statusFilter;
    const matchRating = ratingFilter === 'All' || r.rating === Number(ratingFilter);
    return matchSearch && matchStatus && matchRating;
  });

  // ── actions ────────────────────────────────────────────────────────────────
  const handleApprove = async (review) => {
    if (review.source === 'rating') return; // ratings have no status, nothing to do
    setActionLoading(review.id);
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/reviews/reports/${review.report_id}/`,
        { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status: 'resolved' }) }
      );
      if (!res.ok) throw new Error('Failed to approve');
      await fetchAll();
      if (selectedReview?.id === review.id) setSelectedReview(null);
    } catch (err) {
      alert('Action failed: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleFlag = async (review) => {
    if (review.source === 'rating') return;
    setActionLoading(review.id);
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/reviews/reports/${review.report_id}/`,
        { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status: 'pending' }) }
      );
      if (!res.ok) throw new Error('Failed to flag');
      await fetchAll();
      if (selectedReview?.id === review.id) setSelectedReview(null);
    } catch (err) {
      alert('Action failed: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (review) => {
    if (!window.confirm('Delete this entry permanently?')) return;
    setActionLoading(review.id);
    try {
      const endpoint = review.source === 'rating'
        ? `${API_BASE_URL}/admin/reviews/ratings/${review.id}/`
        : `${API_BASE_URL}/admin/reviews/reports/${review.report_id}/`;

      const res = await fetch(endpoint, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to delete');
      await fetchAll();
      if (selectedReview?.id === review.id) setSelectedReview(null);
    } catch (err) {
      alert('Delete failed: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // ── loading / error states ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
        Loading reviews…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ color: '#dc2626', marginBottom: 12 }}>❌ {error}</div>
        <button
          onClick={fetchAll}
          style={{ background: '#6366f1', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    );
  }

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24 }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>
          💬 Reviews &amp; Reports
        </h2>
        <p style={{ color: '#64748b', marginTop: 4, fontSize: 14 }}>
          Customer reviews, flagged content and suspicious activity
        </p>
      </div>

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total',    value: stats.total,    color: '#6366f1' },
          { label: 'Approved', value: stats.approved, color: '#16a34a' },
          { label: 'Pending',  value: stats.pending,  color: '#ca8a04' },
          { label: 'Flagged',  value: stats.flagged,  color: '#dc2626' },
        ].map(stat => (
          <div key={stat.label} style={{
            background: 'white', borderRadius: 12, padding: '16px 20px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: `4px solid ${stat.color}`
          }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by product, user or comment…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 220, padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none' }}
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, background: 'white', cursor: 'pointer' }}
        >
          <option value="All">All Status</option>
          <option value="Approved">Approved</option>
          <option value="Pending">Pending</option>
          <option value="Flagged">Flagged</option>
        </select>
        <select
          value={ratingFilter}
          onChange={e => setRatingFilter(e.target.value)}
          style={{ padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, background: 'white', cursor: 'pointer' }}
        >
          <option value="All">All Ratings</option>
          {[5, 4, 3, 2, 1].map(n => (
            <option key={n} value={n}>{n} Star{n > 1 ? 's' : ''}</option>
          ))}
        </select>
        <button
          onClick={fetchAll}
          style={{ padding: '10px 16px', background: '#6366f1', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              {['Type', 'Product', 'User', 'Rating', 'Comment', 'Status', 'Date', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((review, i) => {
              const s = STATUS_COLORS[review.status] || STATUS_COLORS.Pending;
              const busy = actionLoading === review.id;
              return (
                <tr key={review.id} style={{
                  borderBottom: '1px solid #f1f5f9',
                  background: review.status === 'Flagged'
                    ? 'rgba(254,242,242,0.5)'
                    : i % 2 === 0 ? 'white' : '#fafafa',
                  opacity: busy ? 0.6 : 1,
                }}>
                  {/* Type badge */}
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      background: review.source === 'report' ? '#fee2e2' : '#ede9fe',
                      color: review.source === 'report' ? '#dc2626' : '#6d28d9',
                      padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                    }}>
                      {review.source === 'report' ? '🚩 Report' : '⭐ Rating'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#1e293b', fontWeight: 500 }}>
                    {review.product}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#475569' }}>
                    {review.user}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 14 }}>
                    {'⭐'.repeat(Math.min(review.rating, 5))}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {review.comment}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: s.bg, color: s.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                      {review.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#94a3b8' }}>
                    {review.date}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => setSelectedReview(review)}
                        disabled={busy}
                        style={{ background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                      >
                        View
                      </button>
                      {review.source === 'report' && (
                        <button
                          title="Approve / Resolve"
                          onClick={() => handleApprove(review)}
                          disabled={busy}
                          style={{ background: '#dcfce7', color: '#16a34a', border: 'none', borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                        >
                          ✅
                        </button>
                      )}
                      {review.source === 'report' && (
                        <button
                          title="Flag / Re-open"
                          onClick={() => handleFlag(review)}
                          disabled={busy}
                          style={{ background: '#fef9c3', color: '#ca8a04', border: 'none', borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                        >
                          🚩
                        </button>
                      )}
                      <button
                        title="Delete"
                        onClick={() => handleDelete(review)}
                        disabled={busy}
                        style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
            No reviews or reports found.
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selectedReview && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={() => setSelectedReview(null)}
        >
          <div
            style={{ background: 'white', borderRadius: 16, padding: 28, width: 480, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {selectedReview.source === 'report' ? '🚩 Report Details' : '⭐ Rating Details'}
              </h3>
              <button onClick={() => setSelectedReview(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>×</button>
            </div>

            {[
              ['Product', selectedReview.product],
              ['User',    selectedReview.user],
              ['Rating',  '⭐'.repeat(Math.min(selectedReview.rating, 5))],
              ['Comment', selectedReview.comment || '—'],
              ['Status',  selectedReview.status],
              ['Date',    selectedReview.date],
              ...(selectedReview.source === 'report' ? [['Reason', selectedReview.reason]] : []),
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: 14 }}>
                <span style={{ color: '#64748b', minWidth: 80, fontWeight: 600 }}>{label}:</span>
                <span style={{ color: '#1e293b' }}>{value}</span>
              </div>
            ))}

            {selectedReview.source === 'report' && (
              <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
                <button
                  onClick={() => handleApprove(selectedReview)}
                  style={{ flex: 1, padding: 10, background: '#dcfce7', color: '#16a34a', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
                >
                  ✅ Resolve
                </button>
                <button
                  onClick={() => handleFlag(selectedReview)}
                  style={{ flex: 1, padding: 10, background: '#fef9c3', color: '#ca8a04', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
                >
                  🚩 Re-Flag
                </button>
                <button
                  onClick={() => handleDelete(selectedReview)}
                  style={{ flex: 1, padding: 10, background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
                >
                  🗑️ Delete
                </button>
              </div>
            )}
            {selectedReview.source === 'rating' && (
              <div style={{ marginTop: 20 }}>
                <button
                  onClick={() => handleDelete(selectedReview)}
                  style={{ width: '100%', padding: 10, background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
                >
                  🗑️ Delete Rating
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ReviewsView;
